import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  SpecialtyPhysicianInfo,
  specialtyPhysicianInfoService,
} from '../../../../services/specialtyPhysicianInfo';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('SpecialtyPhysicianData');

type SpecialtyPhysicianDataScreenRouteProp = RouteProp<RootStackParamList, 'SpecialtyPhysicianData'>;

type RouteParams = {
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string | boolean | number | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else if (typeof value === 'number') {
    displayValue = value.toString();
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const SpecialtyPhysicianDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<SpecialtyPhysicianDataScreenRouteProp>();
  const { targetUserId, viewOnly } = (route.params as RouteParams) || {};
  const [specialtyPhysicianData, setSpecialtyPhysicianData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSpecialtyPhysicianData();
  }, []);

  const loadSpecialtyPhysicianData = async () => {
    try {
      setLoading(true);
      const response = await specialtyPhysicianInfoService.getAllSpecialtyPhysicianInfo(targetUserId);
      logger.debug('[Specialty Physician Data Debug] Loaded all specialty physician entries:', response);

      // Handle response format - check if it's wrapped in a success/data object
      const data = response?.data || response;
      logger.debug('[Specialty Physician Data Debug] Extracted data array:', data);
      logger.debug('[Specialty Physician Data Debug] Data length:', data?.length || 0);

      if (data && Array.isArray(data) && data.length > 0) {
        // Set all specialty physician entries
        logger.debug('[Specialty Physician Data Debug] Setting all specialty physician data entries:', data);
        setSpecialtyPhysicianData(data);
      } else {
        logger.debug('[Specialty Physician Data Debug] No specialty physician entries found or invalid data format');
        setSpecialtyPhysicianData([]);
      }
    } catch (error) {
      logger.error('Error loading specialty physician data:', error);
      Alert.alert('Error', 'Failed to load specialty physician information');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const handleEditSpecialtyPhysician = (specialtyPhysician: any) => {
    const mappedData = {
      id: specialtyPhysician.id,
      fullName: specialtyPhysician.fullName,
      phoneNumber: specialtyPhysician.phoneNumber,
      email: specialtyPhysician.email,
      specialty: specialtyPhysician.specialty,
      officeAddress: specialtyPhysician.officeAddress,
      state: specialtyPhysician.state,
      notes: specialtyPhysician.notes,
    };
    navigation.navigate('SpecialtyPhysicianInput', {
      specialtyPhysicianData: mappedData,
      isEditing: true
    });
  };

  const handleDeleteSpecialtyPhysician = async (specialtyPhysician: any) => {
    Alert.alert('Delete', `Are you sure you want to delete ${specialtyPhysician.fullName}?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          try {
            await specialtyPhysicianInfoService.deleteSpecialtyPhysicianInfo(specialtyPhysician.id);

            // Reload data after deletion
            await loadSpecialtyPhysicianData();

            // Check if there are any remaining specialty physician entries
            const remainingData = await specialtyPhysicianInfoService.getAllSpecialtyPhysicianInfo();
            const actualData = remainingData?.data || remainingData;
            if (!actualData || actualData.length === 0) {
              await tasksService.resetTask('specialty_physicians');
              navigation.navigate('MedicalChecklist');
            }

            Alert.alert('Deleted', 'Specialty physician information deleted.');
          } catch (e) {
            logger.error('Delete specialty physician failed:', e);
            Alert.alert('Error', 'Failed to delete specialty physician information');
          }
        }
      },
    ]);
  };

  const handleAddSpecialtyPhysician = () => {
    navigation.navigate('SpecialtyPhysicianInput');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Specialty Physician Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading specialty physician information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!specialtyPhysicianData || specialtyPhysicianData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Specialty Physician Information</Text>

        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}

        <View style={styles.whiteContainer}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              {viewOnly
                ? "This user has not added specialty physician information yet."
                : "No specialty physician information found"}
            </Text>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addButton}
                onPress={handleAddSpecialtyPhysician}
              >
                <Text style={styles.addButtonText}>Add Specialty Physician Info</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Specialty Physician Information</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View-Only Mode</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {specialtyPhysicianData.map((specialtyPhysician, index) => (
            <View key={specialtyPhysician.id || index} style={styles.physicianCard}>
              <View style={styles.cardHeader}>
                <View style={{flex: 1, flexShrink: 1}}>
                  <Text style={styles.cardName}>
                    {specialtyPhysician.fullName}
                  </Text>
                  <Text style={styles.cardType}>
                    {specialtyPhysician.specialty || 'Specialty not specified'}
                  </Text>
                </View>
                {!viewOnly && (
                  <View style={styles.cardButtons}>
                    <TouchableOpacity
                      style={styles.editCardButton}
                      onPress={() => handleEditSpecialtyPhysician(specialtyPhysician)}
                    >
                      <Text style={styles.editCardButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteCardButton}
                      onPress={() => handleDeleteSpecialtyPhysician(specialtyPhysician)}
                    >
                      <Text style={styles.deleteCardButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              <InfoItem label="Full Name" value={specialtyPhysician.fullName || ''} />
              <InfoItem label="Specialty" value={specialtyPhysician.specialty || ''} />
              <InfoItem label="Phone Number" value={formatPhoneNumber(specialtyPhysician.phoneNumber) || ''} />
              <InfoItem label="Email" value={specialtyPhysician.email || ''} />
              <InfoItem label="Office Address" value={specialtyPhysician.officeAddress || ''} />
              <InfoItem label="State" value={specialtyPhysician.state || ''} />
              <InfoItem label="Notes" value={specialtyPhysician.notes || ''} />
            </View>
          ))}

          {!viewOnly && (
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={styles.addAnotherPhysicianButton}
                onPress={handleAddSpecialtyPhysician}
              >
                <Text style={styles.addAnotherPhysicianButtonText}>Add Another Specialty Physician</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.button, styles.backToListButton]}
                onPress={() => navigation.navigate('MedicalChecklist')}
              >
                <Text style={styles.buttonText}>Back to Checklist</Text>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(10),
    paddingHorizontal: ms(20),
    alignItems: 'center',
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  physicianCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  cardName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  cardType: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editCardButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editCardButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deleteCardButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deleteCardButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherPhysicianButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherPhysicianButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
});
